// src/pages/team/TeamDelegationsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { delegationStatusMap, delegationReasonMap, getStatusMapping, canCancelDelegation, getEntityId, formatDate, getDisplayName, refEntity } from '@/features/team/logic';
import { DelegationFormDialog } from '@/features/team/DelegationFormDialog';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';

type DelegationsTab = 'all' | 'toMe' | 'byMe';

export const TeamDelegationsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const [activeTab, setActiveTab] = useState<DelegationsTab>('all');
  const [delegations, setDelegations] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadDelegations = useCallback(async (tab: DelegationsTab) => {
    if (!id) return;
    try {
      const res = tab === 'toMe'
        ? await store.ListDelegatedToMe(id)
        : tab === 'byMe'
          ? await store.ListMyDelegations(id)
          : await store.ListRoleDelegations(id);
      setDelegations(res.delegations?.delegations ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadDelegations(activeTab);
  }, [id, activeTab, loadDelegations]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleCancel = async (delegationId: string) => {
    if (!id) return;
    try {
      await store.CancelRoleDelegation(id, delegationId, { delegation: refEntity(delegationId), reason: '' });
      showSuccess(t('team.delegationCancelled'));
      loadDelegations(activeTab);
    } catch { /* store handles */ }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.delegations')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="delegations" />


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('team.delegationsTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('team.delegationsDescription')}</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          {t('team.createDelegation')}
        </Button>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <DelegationFormDialog
        open={showCreate}
        teamId={id!}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          showSuccess(t('team.delegationCreated'));
          loadDelegations(activeTab);
        }}
      />

      <div className="flex gap-1 border-b border-border">
        {([
          { id: 'all' as DelegationsTab, label: t('common.all') },
          { id: 'toMe' as DelegationsTab, label: t('team.delegatedToMe') },
          { id: 'byMe' as DelegationsTab, label: t('team.myDelegations') },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.delegator')}</TableHead>
              <TableHead>{t('common.delegate')}</TableHead>
              <TableHead>{t('common.reason')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('common.period')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell>
              </TableRow>
            ) : delegations.length === 0 ? (
              <TableEmpty
                colSpan={6}
                message={
                  activeTab === 'toMe' ? t('team.noDelegatedToMe')
                    : activeTab === 'byMe' ? t('team.noMyDelegations')
                      : t('team.noDelegations')
                }
              />
            ) : (
              delegations.map((del: any) => {
                const delId = getEntityId(del);
                const status = del.status || '';
                const sm = getStatusMapping(delegationStatusMap, status);
                const reasonLabel = delegationReasonMap[del.reason] ? t(delegationReasonMap[del.reason]) : del.reason;

                return (
                  <TableRow key={delId || Math.random()}>
                    <TableCell><span className="text-sm">{getDisplayName(del.delegator)}</span></TableCell>
                    <TableCell><span className="text-sm">{getDisplayName(del.delegate)}</span></TableCell>
                    <TableCell>
                      <Badge variant="primary" size="sm">{reasonLabel}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={sm.variant} size="sm">
                        {t(sm.labelKey)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(del.start_at)} — {formatDate(del.end_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/teams/${id}/delegations/${delId}`}>
                          <Button variant="ghost" size="sm">{t('common.view')}</Button>
                        </Link>
                        {canCancelDelegation(status) && (
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleCancel(delId)}>
                            {t('common.cancel')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TeamDelegationsPage;
